/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import findLast from 'lodash/findLast';

import {
  findAndToggleButtonInfo,
  getKeyForExclusiveQuestion,
  getLegacyKeyForExclusiveQuestion,
  getKeyForMultipleChoiceOption,
  getLinkForExclusiveOption,
  getLinkForMultipleChoiceOption,
  getNegatedButtonInfoForOption,
  getReturnToHereInfo,
  getValueForExclusiveOption,
  getValueForMultipleChoiceOption,
} from './question_utils';
import { laneIsEmpty, laneMatches } from './swimlane_utils';
import {
  ChatMessageType,
  DocumentVersionType,
  FormulaBin,
  HowActivatedType,
  PageOptionExclusiveType,
  PageOptionMultipleChoiceType,
  PageQuestionExclusiveType,
  PageQuestionFreeTextType,
  PageQuestionMultipleChoiceType,
  PageQuestionNumericalEntryType,
  PageQuestionUploadPictureType,
  MessageType,
  PageCalculatorType,
  PageMultipleQuestionsType,
  PageTypeEnum,
  PageSwimLaneType,
  PageType,
  QuestionType,
  ReturnToHereInfo,
  SelectedButtonInfo,
  LinkSlugType,
} from './types';
import { escapeHtml, createRandomId, clearObject, verifyMessageIsAlgorithmPage } from '../utils';
import { landingPage } from '../chat/landingPage';
import { formatScoreNumber } from './calculator_utils';

export type SetLike = { [key: string]: true };

export type RunAlgorithmCallbacks = {
  addReturnToHere: (messageIndex: number, info: ReturnToHereInfo) => void;
  getDocumentVersion: (documentVersionId: string | undefined) => DocumentVersionType | undefined;
  getDocumentVersionByTag: (tag: string) => DocumentVersionType | undefined;
  rollbackToMessage: (
    index: number,
    pageId?: string,
    clearSelected?: boolean
  ) => {
    // Returns the new messages and selected after rollingback
    messages: MessageType[];
    selected: {
      [key: string]: SelectedButtonInfo;
    };
  };
  addToPagesUsed: (param: { pageId?: string; pageName?: string }, docVersionId?: string) => SetLike;
  getMessagesAndSelected: () => { messages: MessageType[]; selected: { [key: string]: SelectedButtonInfo } };
  isPublishedOnly: () => boolean;
  enableNextButton: boolean;
  pagesUsed: () => SetLike;
};

export const CALCULATOR_METAQUESTION_QUESTION_INDEX = 99990;

/**
 * The GOAL of this file is to have a self-contained file that runs an algorithm and contains all the logic.
 *
 * Currently it is not self-contained - for example the logic for the IF statement is in Page.tsx. Instead,
 * the logic should be moved here, and Page etc should just be a view.
 *
 * It is extra important that the functions here are well documented and clean.  It is not there at time of writing.
 *
 * These functions should only be called from chatSlice.ts
 *
 *
 * Example flow:
 *  1. User searches for "Gout".
 *  2. `textInputThunk.fulfilled` will fetch this document, and then call `addChatMessage`,
 *     ready to pass the `documentVersion` when the `cb` is called
 *  3. `addChatMessage` will add the user input "Gout" to messages, get the root (aka landing) page,
 *     and then call `addPageToMessages` to add this page.  This will call 'runPage' to run the page.
 *
 * Say that page was a multiple choice question, and the user now clicks an answer
 *  1. The `chatSlice.toggleOptionKey` is called which updates the `selected` object, and calls
 *     `runPage` to see if we now need to show another page.
 *  2. In the multiple choice example, if the user didn't clear the None button and didn't click a button
 *     with an override link, then it's possible that they will want to click another button. So show a
 *     'next' button, and wait for them to click that (we set the `need_confirmation` flag in the message.extra
 *     and wait for `have_confirmation` to be set to true)
 *
 * Caveats:
 *   - Currently we don't do a good job of actually following the logic above, and instead too much logic is
 *   still being done in Page.tsx.  This is because we have not yet moved the logic from Page.tsx to here.
 *   - This BADLY needs unit testing!!
 */

/**
 * This function adds a page to the messages array, and then runs it.
 *
 * This is called, for example, by addChatMessage (triggered by user typing in a search term) when it knows
 * the page to add.
 *
 * @returns The messageIndex of the new message (might not be last message, because we might have added more than one message)
 */
export function addPageToMessages(
  {
    page,
    seenPageId,
  }: {
    page: PageType;
    seenPageId?: Set<string>; // For checking for circular references
  },
  cb: RunAlgorithmCallbacks
) {
  const { messages, selected } = cb.getMessagesAndSelected();
  const startMessageIndex = messages.length;
  const message: MessageType = {
    is_sent: false,
    pages: [page],
    pagesUsed: Object.keys(cb.addToPagesUsed({ pageId: page.id, pageName: page.name }, page.document_version_id)),
    selected: { ...selected },
    index: startMessageIndex,
    extra: {
      type: 'ALGORITHM_PAGE' as const,
      need_confirmation: false, // Will set this correctly in runPage
      have_confirmation: false,
      has_only_leaf_pages: true, // Will set this correctly in runPage
    } as const,
  };
  messages.push(message);
  // Now run the page that we just added
  const ret = runPage({ page, messageIndex: startMessageIndex, seenPageId }, cb);
  // If we get an error here, add it to the message
  if (ret.error) {
    (message.extra as any).algorithm_error = ret.error;
  }

  const newMessages = messages.slice(startMessageIndex);

  return { startMessageIndex, newMessages, error: ret.error };
}

export function rollbackAndRunPage(
  {
    messageIndex,
    clearSelected,
  }: {
    messageIndex: number; // The index of the message that we are about to run
    clearSelected?: boolean;
  },
  cb: RunAlgorithmCallbacks
) {
  cb.rollbackToMessage(messageIndex, undefined, clearSelected);
  if (messageIndex < 0) {
    return;
  }
  const page = cb.getMessagesAndSelected().messages[messageIndex]?.pages?.[0];
  if (!page) return;
  runPage({ page, messageIndex }, cb);
}

/** This is an internal function to "run" a page - i.e. evaluate it and add any pages to the chat messages
 *  that follow.
 *
 *  This is internal - use `addPageToMessages()` or `rollbackAndRunPage()` for the public functions.
 */
function runPage(
  {
    page,
    last_key,
    last_value,
    messageIndex,
    seenPageId: seenPageId_, // For checking for circular references
  }: {
    page: PageType;
    last_key?: string; // Just for debug - optionally last key toggled
    last_value?: string; // ditto
    messageIndex: number; // The index of the message that we are about to run
    seenPageId?: Set<string>; // For checking for circular references
  },
  cb: RunAlgorithmCallbacks
): {
  found_selected_key: boolean;
  error?: string;
} {
  const { messages, selected } = cb.getMessagesAndSelected();
  const message = messages[messageIndex]!;
  const seenPageId = seenPageId_ || new Set<string>();
  if (seenPageId.has(page.id)) {
    console.error('Circular reference detected to page:', page.name, page.id);
    return { found_selected_key: false, error: `Circular reference detected to page: ${page.name}` };
  }
  seenPageId.add(page.id);
  /** In a few rare cases, we could have, say, two swimlanes that match, and each opens
   *  an external link.  We don't want to show both, but only one.  But we don't want to rollback
   *  messages if we get here, because we might have already shown a message that we want to keep.
   *  So instead just keep track of hasMoreMessages, and don't add any more messages.
   */
  const dontShowMoreMessages = messages.length > messageIndex + 1;

  cb.addToPagesUsed({ pageId: page.id, pageName: page.name }, page.document_version_id);
  const returnToHereInfo = getReturnToHereInfo(page);
  if (returnToHereInfo) {
    cb.addReturnToHere(messageIndex, returnToHereInfo);
  }

  if (page.pageType === PageTypeEnum.MULTIPLE_QUESTIONS || page.pageType === PageTypeEnum.CALCULATOR) {
    refreshSelected(messageIndex, page.questions, selected);
  }

  let {
    skipToPage,
    found_selected_key,
    pageLinks,
    hide_sent,
    error,
    addSelectedKeys,
    removeSelectedKeys,
    need_confirmation,
    isLeafPage,
  } = getNextPageLinks(page, messageIndex, selected, last_key);
  Object.assign(selected, addSelectedKeys); // Note we are updating selected
  if (removeSelectedKeys) {
    removeSelectedKeys.forEach((key) => {
      delete selected[key];
    });
  }
  if (message.extra?.type === 'ALGORITHM_PAGE' && !isLeafPage) {
    message!.extra.has_only_leaf_pages = false;
  }
  message!.selected = { ...selected };
  need_confirmation &&= cb.enableNextButton;

  /** Now run through all the pages that this message will include.
   *
   *  For example, a message contains a page that includes another page at the end,
   *  inside the same message bubble.
   */
  pageLinks?.forEach((link) => {
    const included_page = getPage(link, page.document_version_id, cb);
    if (included_page) {
      if (seenPageId.has(included_page.id)) {
        error ||= `Circular reference detected from page: "${page.name}" to page "${included_page.name}"`;
        console.warn(error);
      } else {
        message!.pages!.push(included_page);
        const res = runPage({ page: included_page, last_key, last_value, messageIndex, seenPageId }, cb);
        found_selected_key ||= res.found_selected_key;
        error ||= res.error;
      }
      // This will call cb.addToPagesUsed for us
    } else {
      // Add anyway - for external links, and to show it as a missing page, etc
      cb.addToPagesUsed({ pageName: link }, page.document_version_id);
    }
  });
  message.pagesUsed = Object.keys(cb.pagesUsed());
  // 'Run' the page, and see if we need to go on to the next page etc.
  if (page.pageType === PageTypeEnum.MULTIPLE_QUESTIONS || page.pageType === PageTypeEnum.CALCULATOR) {
    verifyMessageIsAlgorithmPage(message);

    message.extra.need_confirmation = !!need_confirmation;
    if (error) {
      message.extra.cant_confirm_error = error;
      console.log('Error in runPage:', error);
    } else {
      delete message.extra.cant_confirm_error;
    }
    if (need_confirmation && !message.extra.have_confirmation) {
      skipToPage = undefined;
    }
  }

  if (skipToPage) {
    if (dontShowMoreMessages) {
      console.log('We want to show a page, but we already have a message that we want to keep. Not showing both');
    } else {
      const ret = addChatMessage(
        {
          message: {
            text: skipToPage?.link!,
            link: skipToPage,
            document_version_id: page.document_version_id,
          },
          hide_sent,
          howActivated: 'algorithmClick',
          seenPageId, // For checking for circular references
        },
        cb
      );
      error ||= ret?.error;
    }
  }
  return { found_selected_key: !!found_selected_key, error };
}

/** We add a message because:
 *  1. The user typed a message (message.humanTyped will be true)
 *  2. We are showing page from a guide or algorithm
 *  3. We are showing a response that we found zero, one or more matches. And message.alternative_document_versions will be set
 */
export function addChatMessage(
  {
    hide_sent,
    message,
    specificPage,
    searchId: _searchId,
    howActivated,
    seenPageId,
  }: {
    hide_sent?: boolean;
    message: ChatMessageType;
    specificPage?: string; // To override showing the root page, jump to this page instead
    searchId?: string;
    howActivated: HowActivatedType;
    seenPageId?: Set<string>; // For checking for circular references
  },
  cb: RunAlgorithmCallbacks
) {
  let next_page_link = (message.link?.link || message.text)?.trim();
  const { messages, selected } = cb.getMessagesAndSelected();
  const isExternalLink = next_page_link[0] === '#';
  let document_version_id = message.document_version_id;
  specificPage = specificPage?.trim();
  if (isExternalLink) {
    // pagesUsed is normally just id's, but since we don't have an id, just use the title instead
    const pagesUsed = cb.addToPagesUsed({ pageName: next_page_link }, document_version_id);
    // Normally we should show a "Open: foo" message when we link to an external link, but there's one exception:
    // If the mesage is a MULTIPLE_QUESTIONS page and there are no questions, then we don't want to show the 'Open: foo'
    // Because it's ends up looking confusing.  Instead we want to 'mush' the external link into the previous message.
    // Do so with css, and do that by just adding a flag to the OPEN_LINK message

    // Must make sure we clear the selected so that we don't bring the currently selected options
    // into the next algorithm
    // See https://pearl-project.atlassian.net/browse/PRL-2861
    clearObject(selected);
    messages.push({
      is_sent: true,
      index: messages.length,
      pagesUsed: Object.keys(pagesUsed),
      hide_sent: !!hide_sent, // Need to still send the message, so that we actually open the new document
      extra: {
        type: 'OPEN_LINK',
        external_link:
          message.link?.link || message.link?.link_slug ? message.link : { link: next_page_link, link_slug: undefined },
      },
    });
    return;
  } else {
    messages.push({
      is_sent: true,
      content: escapeHtml(message.text),
      index: messages.length,
      selected: { ...selected },
      hide_sent: !!hide_sent, // Need to still send the message so that we have somewhere to scroll to
    });
  }

  const searchId = _searchId || createRandomId();
  const documentVersion = cb.getDocumentVersion(document_version_id);
  /* The link uses the text if the link is not set */

  let next_page_id: string | undefined = undefined;
  if (message.humanTyped) {
    const tag = next_page_link.toLowerCase();
    document_version_id ||= cb.getDocumentVersionByTag(tag)?.id;
    next_page_id = 'LANDING';
    if (specificPage) {
      // This is for when we have a url like  "#Gout#someparticularpage"
      // So instead of going to the landing page, go to that specific page instead
      next_page_id = documentVersion?.nameToPageId[specificPage];
      next_page_link = specificPage;
    }
    if (document_version_id == null || !documentVersion) {
      // See InnerMessageExtra for how this is rendered
      messages.push({
        is_sent: false,
        pagesUsed: Object.keys(cb.pagesUsed()),
        index: messages.length,
        extra: {
          type: 'NO_SEARCH_MATCH',
          publishedOnly: cb.isPublishedOnly(),
          howActivated,
          searchTerm: next_page_link,
          searchId /* Random id For analytics */,
        },
      });

      return;
    }
  } else {
    next_page_id = documentVersion?.nameToPageId[next_page_link];
  }
  if (next_page_id == null) {
    /* Page is missing - show warning and/or let them create a new page */
    console.log(`No page found!!: "${next_page_link}"`);
    messages.push({
      is_sent: false,
      pagesUsed: Object.keys(cb.addToPagesUsed({ pageName: next_page_link }, document_version_id)),
      pages: document_version_id
        ? [
            {
              id: '',
              pageType: PageTypeEnum.MULTIPLE_QUESTIONS,
              name: next_page_link,
              document_version_id,
              content: '',
              isRoot: false,
              questions: [],
            },
          ]
        : undefined,
      content: "(No page '" + next_page_link + "' has been added yet, sorry)",
      index: messages.length,
    });
    return;
  }

  const page: PageType | undefined =
    next_page_id === 'LANDING' ? landingPage(documentVersion) : documentVersion?.pages[next_page_id as string];
  if (!page) {
    messages.push({
      is_sent: false,
      pagesUsed: Object.keys(cb.addToPagesUsed({ pageId: next_page_id }, document_version_id)),
      no_bubble: true,
      index: messages.length,
      extra: {
        type: 'NO_PAGES',
      },
    });
  } else {
    const ret = addPageToMessages({ page, seenPageId }, cb);
    if (ret.error) {
      return { error: ret.error };
    }
  }
}

/**
 *  Go through all the questions on a page, and set any missing selected options to no.
 *
 *  Also correctly set the hideFromSummary.  This is for hiding options that we do not want
 *  to show in the summary text that we show to the user, but still want to set the variable.
 *  For example, if you have the options:
 *
 *  What pets do you have?
 *    [ Cat, Dog, None]
 *
 * And the user presses "None", then we don't want to show "Not Cat, Not Dog", because that's obvious
 * if you chose None.  And if the users presses "Cat", we want to show "Not Dog" but do not show "Not None".
 */
function refreshSelected(
  messageIndex: number,
  questions: QuestionType[] | undefined,
  selected: { [key: string]: SelectedButtonInfo }
) {
  if (!questions) return;
  questions.forEach((question, questionIndex) => {
    if (!questionCanHaveOptions(question)) {
      return;
    }
    const options = question.options;

    const hasAnyOverrides = options.some((option) => {
      if (!option.link) return false;
      const value = getValueForMultipleChoiceOption(option);
      return value && selected[getKeyForMultipleChoiceOption(option)]?.value === value;
    });
    const noneOption = options[options.findIndex((option) => option.isNone)];
    const noneSelected = noneOption ? selected[getKeyForMultipleChoiceOption(noneOption!)] : undefined;
    const isNoneSelected: boolean =
      !!noneSelected && !!noneOption && noneSelected.value === getValueForMultipleChoiceOption(noneOption);
    if (question.type === 'multiple_choice') {
      // console.log('q:', questionIndex, 'noneIndex:', noneIndex, isNoneSelected);
      options.forEach((option, optionIndex) => {
        const key = getKeyForMultipleChoiceOption(option);
        if (selected[key] && selected[key]!.value !== 'NO') {
          selected[key]!.hideFromSummary = false;
        } else {
          // Hide from selected if:
          //  - It is not selected and we've clicked any option with an override link, or
          //  - This option is none and it is NOT selected, or
          //  - This option is NOT none and None is selected
          const hideFromSummary = hasAnyOverrides || isNoneSelected !== !!option.isNone;
          // console.log('hideFromSummary', hideFromSummary, hasAnyOverrides, isNoneSelected, option.isNone);
          if (!selected[key]) {
            selected[key] = getNegatedButtonInfoForOption(
              question,
              option,
              messageIndex,
              questionIndex,
              optionIndex,
              hideFromSummary
            );
          } else {
            // (selected[key]!.value === 'NO') {
            selected[key]!.hideFromSummary = hideFromSummary;
          }
        }
      });
    }
  });
}

export function shouldSwimlaneShowFallback(page: PageSwimLaneType, selected: { [key: string]: SelectedButtonInfo }) {
  if (!page.lanes) return true;
  return page.lanes?.every((lane, index) => {
    return !laneMatches(page.lanes!, index, selected) || laneIsEmpty(page.lanes!, index);
  });
}

/** Whether this page type supports the next_link and next_link_slug */
export function pageCanHaveNextLink(
  page: PageType
): page is PageMultipleQuestionsType | PageCalculatorType | PageSwimLaneType {
  return (
    page.pageType === PageTypeEnum.MULTIPLE_QUESTIONS ||
    page.pageType === PageTypeEnum.CALCULATOR ||
    page.pageType === PageTypeEnum.SWIMLANE
  );
}

/** Returns true if this question is multiple choice or single choice as so has options (buttons).
 *  Returns false if this question is a number or text input, and so doesn't have options.
 *
 *  Note this has a typescript hack to make it work with the typescript 4 which gets confused about
 *  union arrays.  So I've had to add the & { options: ... } to the return type to make typescript
 *  happy. When we upgrade to typescript 5, the "& { options: ... }" can be removed.
 */
export function questionCanHaveOptions(question: QuestionType): question is (
  | PageQuestionMultipleChoiceType
  | PageQuestionExclusiveType
) & {
  // FIXME: Remove this when we upgrade to typescript 5
  options: (PageOptionMultipleChoiceType | PageOptionExclusiveType)[];
} {
  return question.type === 'multiple_choice' || question.type === 'exclusive';
}

export function questionCanHaveToggleableRequired(
  question: QuestionType
): question is PageQuestionFreeTextType | PageQuestionNumericalEntryType | PageQuestionUploadPictureType {
  return question.type === 'free_text' || question.type === 'numerical_entry' || question.type === 'upload_picture';
}

export function toggleOptionKey(
  {
    page,
    messageIndex,
    key: key_,
    value: value_,
  }: {
    page: PageType;
    messageIndex: number;
    key: string;
    value: string;
  },
  cb: RunAlgorithmCallbacks
) {
  if (page.pageType !== PageTypeEnum.MULTIPLE_QUESTIONS && page.pageType !== PageTypeEnum.CALCULATOR) {
    console.warn('toggleOptionKey for a page that is not a question page!?');
    return;
  }
  if (!page.questions) {
    console.warn('toggleOptionKey for a page with no questions!?');
    return;
  }

  const key = key_.toUpperCase();
  const value = value_.toUpperCase();
  // We MUST rollback to the messageIndex before we check state.selected, because it might have changed
  const { messages, selected } = cb.rollbackToMessage(messageIndex, page.id);
  const lastMessage = messages[messages.length - 1];
  if (lastMessage?.extra?.type === 'ALGORITHM_PAGE') {
    lastMessage.extra.have_confirmation = false;
  }
  // Find AND TOGGLE the button
  // It's not very nice to have both finding and toggling in one function, but it's a bit tricky to separate them
  const buttonInfo = findAndToggleButtonInfo(page, messageIndex, key, value, value_, selected[key]?.value === value);
  if (!buttonInfo) {
    console.warn('toggleOptionKey unable to find button with key value:', key, value);
    return;
  }

  const question = page.questions?.[buttonInfo.questionIndex!];
  const options =
    question?.type === 'exclusive' || question?.type === 'multiple_choice' ? question?.options : undefined;
  const canonicalKey =
    question?.type === 'exclusive'
      ? getKeyForExclusiveQuestion(question, page)
      : question?.type === 'multiple_choice'
      ? question.options?.[buttonInfo.optionIndex!]
        ? getKeyForMultipleChoiceOption(question.options[buttonInfo.optionIndex!]!)
        : key
      : question?.id?.toUpperCase() || key;

  // If this option is 'None' then we should unselect all the other options.
  // If this option is not None, then we should unselect the None option.
  options?.forEach((option, optionIndex) => {
    if (buttonInfo?.isNone || option.isNone) {
      // Just delete, and fill it back in in the refreshSelected function in runPage
      delete selected[getKeyForMultipleChoiceOption(option)];
    }
  });
  delete selected[key];
  selected[canonicalKey] = buttonInfo;
  if (question?.type === 'free_text' && !question?.required) {
    // Add a helper selected key for whether this free_text question has been answered
    selected[`has_${canonicalKey}`] = {
      ...buttonInfo,
      value: buttonInfo.value.trim() ? 'YES' : 'NO',
      hideFromSummary: true,
      summarytext: { keyStr: 'Has ' + buttonInfo.summarytext, valueStr: '' },
    };
  }
  if (question?.type === 'numerical_entry' && !question?.required) {
    // Add a helper selected key for whether this numerical_entry question has been answered
    selected[`has_${canonicalKey}`] = {
      ...buttonInfo,
      value: buttonInfo.value != null ? 'YES' : 'NO',
      hideFromSummary: true,
      summarytext: { keyStr: 'Has ' + buttonInfo.summarytext, valueStr: '' },
    };
  }
  // We have now rolled back to the message clicked on, and updated the selected choices.
  // Now, lets 'run' the page, and see if we need to go on to the next page etc.
  const { error, found_selected_key } = runPage(
    {
      page,
      last_key: key,
      last_value: buttonInfo.value,
      messageIndex,
    },
    cb
  );
  if (key && !found_selected_key && !error) {
    console.warn(`Page has no option with key "${key}" and value "${buttonInfo.value}"`);
  }
}

/** Reduces the messages array length to the given index, and returns the new selected button options */
export const rollbackToMessageHelper = (
  index: number,
  pageId: string | undefined,
  messages: MessageType[],
  returnToHere: { messageIndex: number; info: ReturnToHereInfo }[],
  clearSelected?: boolean
) => {
  if (index >= messages.length) {
    throw new Error('rollbackToMessageHelper: index out of range');
  }
  // console.log('rollbackToMessageHelper', index);
  const lastMessage = messages[index];
  if (messages.length !== index + 1) {
    messages.length = index + 1;
    if (lastMessage?.extra?.type === 'ALGORITHM_PAGE') {
      // Only unset if actually rolling back
      lastMessage.extra.have_confirmation = false;
    }
  }
  if (clearSelected && index >= 0) {
    lastMessage!.selected = {};
  }
  if (lastMessage?.pages && lastMessage!.pages.length > 1) {
    const index = lastMessage!.pages.findIndex((page) => page.id === pageId);
    lastMessage!.pages.length = Math.max(1, index + 1);
    // Run page will re-add the rest
  }

  const selected = clearSelected
    ? {}
    : JSON.parse(JSON.stringify(findLast(messages, (message: MessageType) => !!message.selected)?.selected || {}));
  returnToHere = returnToHere.filter((x) => x.messageIndex <= index);

  const pagesUsedArray = (findLast(messages, (message) => message.pagesUsed) as MessageType)?.pagesUsed || [];
  const pagesUsed = arrayToSetLike(pagesUsedArray);

  return { messages, selected, returnToHere, pagesUsed };
};

/** Whether this page type allows page.content */
export function pageTypeAllowsContent(pageType: PageTypeEnum | undefined): boolean {
  return (
    pageType !== PageTypeEnum.SWIMLANE &&
    pageType !== PageTypeEnum.TABS &&
    pageType !== PageTypeEnum.IF &&
    pageType !== PageTypeEnum.JSON
  );
}

export function pageTypeAllowsTooltip(pageType: PageTypeEnum | undefined): boolean {
  return (
    pageType !== PageTypeEnum.SWIMLANE &&
    pageType !== PageTypeEnum.TABS &&
    pageType !== PageTypeEnum.IF &&
    pageType !== PageTypeEnum.JSON
  );
}

export function pageTypeAllowsTrusana(pageType: PageTypeEnum | undefined): boolean {
  return pageType !== PageTypeEnum.JSON;
}

/** Content to display in creator when there's a page with no content, to make it easier to see the logical flow in creator */
export function pagePlaceholderContentInCreator(page: PageType | undefined): string | undefined {
  if (
    (page?.pageType === PageTypeEnum.MULTIPLE_QUESTIONS || page?.pageType === PageTypeEnum.CALCULATOR) &&
    !page.questions?.length &&
    !page.content
  )
    return `'${page?.name}' is empty`;
  if (page?.pageType === PageTypeEnum.IF) return `'${page?.name}' is a logical 'if' statement and will not be shown`;
  return undefined;
}

/**
 * For the given page, return the list of pages that should be shown inside
 * the given page.
 *
 * Or returns any error message that we want to display for this page when in creator mode.
 */

function link(
  link: string | undefined,
  link_slug: string | undefined,
  _skipToPage: LinkSlugType | undefined,
  _pageLinks: string[]
): { _skipToPage: LinkSlugType | undefined; _pageLinks: string[] } {
  if (!link) {
    return { _skipToPage, _pageLinks };
  }

  if (link.startsWith('#')) {
    _skipToPage ||= { link, link_slug };
  } else {
    _pageLinks.push(link);
  }

  return { _skipToPage, _pageLinks };
}

export function getNextPageLinks(
  page: PageType,
  messageIndex: number,
  selectedKeys:
    | {
        [key: string]: SelectedButtonInfo;
      }
    | undefined,
  look_for_selected_key?: string // For debug use
): {
  pageLinks?: string[]; // both internal (page) and external (doc) links
  skipToPage?: LinkSlugType;
  error?: string;
  hide_sent?: boolean;
  comment?: string;
  found_selected_key?: boolean; // For debug use
  anyUnselected?: boolean;
  addSelectedKeys?: { [key: string]: SelectedButtonInfo };
  removeSelectedKeys?: string[];
  need_confirmation?: boolean;
  laneContents?: string[];
  isLeafPage?: boolean; // true is a multiple choice question with no options and no next_link starting with #
} {
  let isLeafPage = false; // Has no links to any other pages
  let pageLinks: string[] = [];
  const laneContents: string[] = [];
  let addSelectedKeys: { [key: string]: SelectedButtonInfo } | undefined = undefined;
  let removeSelectedKeys: string[] | undefined = undefined;
  switch (page.pageType) {
    case PageTypeEnum.IF:
      if (!selectedKeys) {
        return { error: 'No options selected yet' };
      }
      if (!page.variable) {
        return { error: 'Logical-if does not have a variable configured' };
      }
      const selected = selectedKeys?.[page.variable!];
      if (!selected) {
        return { error: 'Question has not been answered yet' };
      }
      const next_link = selected.value === 'YES' ? page.true_link?.trim() : page.false_link?.trim();
      const next_link_slug = selected.value === 'YES' ? page.true_link_slug : page.false_link_slug;
      if (!next_link) {
        return { error: 'Logical-if has not have the next-page configured' };
      }

      if (next_link.startsWith('#')) {
        return { skipToPage: { link: next_link, link_slug: next_link_slug } };
      } else {
        return { pageLinks: [next_link] };
      }
    case PageTypeEnum.SWIMLANE: {
      if (!selectedKeys) {
        return { error: 'No questions answered' };
      }
      let skipToPage: LinkSlugType | undefined = undefined;
      // function link(link: string | undefined, link_slug: string | undefined, skipToPage:LinkSlugType | undefined , pageLinks: string[]) {
      //   if (!link) return;
      //   if (link.startsWith('#')) {
      //     skipToPage ||= { link, link_slug };
      //   } else {
      //     pageLinks.push(link);
      //   }
      // }

      // Check which lanes will match
      page.lanes?.forEach((lane, index) => {
        if (laneMatches(page.lanes!, index, selectedKeys)) {
          if (lane.content) laneContents.push(lane.content);
          ({ _pageLinks: pageLinks, _skipToPage: skipToPage } = link(
            lane.link!,
            lane.link_slug,
            skipToPage,
            pageLinks
          ));
        }
      });
      if (page.fallback_link && shouldSwimlaneShowFallback(page, selectedKeys)) {
        ({ _pageLinks: pageLinks, _skipToPage: skipToPage } = link(
          page.fallback_link,
          page.fallback_link_slug,
          skipToPage,
          pageLinks
        ));
      }
      if (page.next_link) {
        ({ _pageLinks: pageLinks, _skipToPage: skipToPage } = link(
          page.next_link,
          page.next_link_slug,
          skipToPage,
          pageLinks
        ));
      }
      let error: string | undefined = undefined;
      if (!pageLinks?.length && !laneContents?.length && !skipToPage) {
        if (Object.keys(selectedKeys).length === 0) {
          error = 'No questions answered';
        } else {
          error = 'Sorry, this combination is currently missing in the algorithm';
        }
      }

      return { skipToPage, pageLinks, laneContents, error };
    }
    case PageTypeEnum.MULTIPLE_QUESTIONS: {
      let hide_sent = false;
      let error: undefined | string = undefined;
      if (!page.questions?.length && !page.next_link?.startsWith('#')) {
        isLeafPage = true;
      }
      let { anyUnselected, anyUnselectedAboveSkipToPage, skipToPage, found_selected_key, need_confirmation } =
        checkAllQuestionsAnswered(page, selectedKeys, look_for_selected_key);
      // This is really useful for copy-pasting into unit tests
      // console.log(
      //   'checkAllQuestionsAnswered called with:',
      //   { page: JSON.stringify(page), selectedKeys: JSON.stringify(selectedKeys), look_for_selected_key },
      //   'result:',
      //   {
      //     anyUnselected,
      //     anyUnselectedAboveSkipToPage,
      //     skipToPage,
      //     found_selected_key,
      //     need_confirmation,
      //   }
      // );
      // If we have selected an option with a link, then we should jump to that page regardless of any other
      // questions.
      // But if not, then we should go to next_link only if all questions have been answered.
      // (multiple_choice questions are always treated as unanswered.)
      if (!skipToPage) {
        if (
          (page.questions?.length! >= 1 && !anyUnselected) ||
          (!page.questions?.length && page.next_link?.startsWith('#'))
        ) {
          if (page.next_link) {
            skipToPage = { link: page.next_link, link_slug: page.next_link_slug };
            hide_sent =
              !!(!page.questions?.length && page.next_link.startsWith('#')) ||
              !(page.questions?.length === 1 && page.questions[0]!.type === 'exclusive');
            // need_confirmation will tell use whether we need to ask the user to confirm before showing
            // the next page (skipToPage)
          } else if (page.questions?.length) {
            error = `Page "${page.name}" has ${page.questions?.length} question${
              page.questions.length > 1 ? 's' : ''
            } but no link to the next page.`;
            need_confirmation = false;
          }
        } else if (page.next_link && !page.questions?.length && !page.next_link.startsWith('#')) {
          // We want to embed the next link in the message, not show it as a separate message
          pageLinks.push(page.next_link);
          // This is embedded - no need for confirmation
          need_confirmation = false;
        }
      } else if (anyUnselectedAboveSkipToPage) {
        // Use anyUnselectedAboveSkipToPage if we want to require only some of questions to be answered before we skip to the next page
        // use anyUnselected if we want to require all questions to be answered before we skip to the next page
        skipToPage = undefined;
        need_confirmation = false;
      }
      return {
        pageLinks,
        skipToPage,
        hide_sent,
        found_selected_key,
        anyUnselected,
        error,
        need_confirmation,
        isLeafPage,
      };
    }
    case PageTypeEnum.CALCULATOR: {
      let { anyUnselected, skipToPage, pointsArray, found_selected_key, need_confirmation } = checkAllQuestionsAnswered(
        page,
        selectedKeys,
        look_for_selected_key
      );
      let comment: string | undefined = undefined;
      const points_key = `points_${page.name.toLocaleUpperCase()}`;
      const bin_key = `bin_${page.name.toLocaleUpperCase()}`;
      if (!anyUnselected) {
        // Find which bin the number of points falls into
        if (!page.formulaBins && !page.next_link) {
          removeSelectedKeys = [points_key, bin_key];
          return { error: 'Calculator formula bins not defined', found_selected_key };
        } else if (!page.formulaBins) {
          removeSelectedKeys = [bin_key];
        }
        try {
          const points = runCalculatorFormula(page.formula, pointsArray!);
          // print points to 0,1 or 2 decimal places
          comment = `score = ${formatScoreNumber(points)}`;
          addSelectedKeys = {
            [points_key]: {
              value: formatScoreNumber(points),
              messageIndex,
              summarytext: { keyStr: page.name, valueStr: comment },
              questionIndex: CALCULATOR_METAQUESTION_QUESTION_INDEX,
              points,
              hideScore: page.hideScores,
              ...(page.hideScores ? { hideFromSummary: true } : {}),
            },
          };
          const firstBin = page.formulaBins ? getMatchingBin(page.formulaBins, points!) : undefined;
          if (!firstBin && !page.next_link) {
            console.log('No bin found for points:', points, 'bins:', page.formulaBins);
            return { error: `${comment} has no formula bin` };
          } else if (firstBin) {
            if (firstBin.link) {
              skipToPage ||= { link: firstBin.link, link_slug: firstBin.link_slug };
            }
            addSelectedKeys[bin_key] = {
              value: firstBin.name,
              messageIndex,
              summarytext: { keyStr: page.name, valueStr: firstBin.name },
              questionIndex: CALCULATOR_METAQUESTION_QUESTION_INDEX + 1,
            };
          }
        } catch (e: any) {
          return { error: `Problem with formula: ${e.message}`, found_selected_key };
        }
        if (!skipToPage && page.next_link) {
          // So no bin link and no override link.  Use page.next_link
          skipToPage = { link: page.next_link, link_slug: page.next_link_slug };
        }
      } else {
        removeSelectedKeys = [points_key, bin_key];
      }
      return {
        skipToPage,
        anyUnselected,
        comment: page.hideScores ? undefined : comment,
        addSelectedKeys,
        removeSelectedKeys,
        found_selected_key,
        need_confirmation,
      };
    }
    case PageTypeEnum.GUIDE:
    case PageTypeEnum.TABS:
    case PageTypeEnum.JSON:
      // Nothing to do
      return {};
    default:
      // @ts-ignore
      return { error: `Page type not supported: ${page.pageType}` };
  }
}

export function doesBinMatch(bin: FormulaBin, points: number): boolean {
  return (
    (bin.min == null || points >= bin.min) &&
    (bin.max == null || (bin.max_is_inclusive ? points! <= bin.max : points < bin.max))
  );
}

export function getMatchingBin(bins: FormulaBin[], points: number): FormulaBin | undefined {
  return bins.find((bin) => doesBinMatch(bin, points));
}

/**
 *
 *  @return {
 *   need_confirmation // Whether we need to ask the user to confirm their selection with
 *                       // a 'next' button before moving on to the skipToPage
 *   skipToPage // The page to skip to IF there's an override link. The page.next_link isn't returned here
 *  }
 */
export function checkAllQuestionsAnswered(
  page: PageMultipleQuestionsType | PageCalculatorType,
  selectedKeys: { [key: string]: SelectedButtonInfo } | undefined,
  look_for_selected_key?: string // For debug use
): {
  anyUnselected: boolean;
  anyUnselectedAboveSkipToPage: boolean; // If skipToPage is true, this is whether there are any unselected questions above the skipToPage
  skipToPage: LinkSlugType | undefined;
  pointsArray?: number[];
  found_selected_key?: boolean; // Whether we have selected the given key. For debug use
  need_confirmation: boolean;
} {
  let skipToPage: LinkSlugType | undefined = undefined;
  const pointsArray: number[] = [];
  let need_confirmation = false;
  let anyUnselected = false;
  let anyUnselectedAboveSkipToPage = false;
  let found_selected_key: boolean | undefined = look_for_selected_key ? false : undefined;
  if (page.questions && selectedKeys) {
    page.questions.forEach((question, questionIndex) => {
      const isLastQuestion = questionIndex === page.questions!.length - 1;
      let points = 0;
      switch (question.type) {
        case 'multiple_choice':
          let found = false;
          question.options.forEach((option, optionIndex) => {
            const key = getKeyForMultipleChoiceOption(option);
            if (look_for_selected_key && key === look_for_selected_key) {
              found_selected_key = true;
            }
            const currentValue = selectedKeys[key]?.value?.toUpperCase();
            const optionSelected = currentValue === getValueForMultipleChoiceOption(option);
            if (optionSelected) {
              found = true;
              const override_link = getLinkForMultipleChoiceOption(option);
              if (override_link) {
                skipToPage ||= override_link;
              }
              // Only show confirmation if the last question is multiple, and has more than one option, and user hasn't selected None
              if (!option.isNone && question.options.length > 1 && isLastQuestion) {
                need_confirmation = true;
              }
              if (option.points) {
                points += option.points;
              }
            }
          });
          if (!found) {
            anyUnselected = true;
          }
          break;
        case 'exclusive':
          // Check if this question has all the answers selected,
          // Or if any question selected has an 'override link' - meaning that we
          // jump to that link regardless of what other questions say
          const key = getKeyForExclusiveQuestion(question, page);
          const legacyKey = getLegacyKeyForExclusiveQuestion(question, page).toUpperCase();
          if (look_for_selected_key && (key === look_for_selected_key || legacyKey === look_for_selected_key)) {
            found_selected_key = true;
          }
          const currentValue = (selectedKeys[key]?.value || selectedKeys[legacyKey]?.value)?.toUpperCase();
          if (!question.options) break;
          if (currentValue) {
            const option = question.options.find((option) => getValueForExclusiveOption(option) === currentValue);
            if (!option) {
              anyUnselected = true;
            } else {
              skipToPage ||= getLinkForExclusiveOption(option, page);
              if (option.points) {
                points += option.points;
              }
            }
          } else {
            anyUnselected = true;
          }
          break;
        case 'numerical_entry': {
          // The value is actually the points.  E.g. If we ask the user's age, then
          // use that value (their age) as the points for the formula
          // Also the key is the question's id
          const key = question.id?.toUpperCase();
          if (look_for_selected_key && key === look_for_selected_key) {
            found_selected_key = true;
          }
          const currentValue = selectedKeys[key]?.value;
          if (currentValue) {
            points = parseFloat(currentValue);
          } else if (question.required) {
            anyUnselected = true;
          }
          if (isLastQuestion) {
            need_confirmation = true;
          }
          break;
        }
        case 'free_text': {
          // If the user has entered something, it's 1, otherwise 0
          const key = question.id?.toUpperCase();
          if (look_for_selected_key && key === look_for_selected_key) {
            found_selected_key = true;
          }
          const currentValue = selectedKeys[key]?.value;
          points = currentValue ? 1 : 0;

          if (!currentValue && question.required) {
            anyUnselected = true;
          }
          if (isLastQuestion) {
            need_confirmation = true;
          }
          break;
        }
        default:
          // Unknown question - e.g. text input etc
          if (isLastQuestion) {
            need_confirmation = true;
          }
          break;
      }
      pointsArray[questionIndex] = points;
      if (!skipToPage && anyUnselected) {
        anyUnselectedAboveSkipToPage = true;
      }
    });
  }
  if (anyUnselected && !skipToPage) {
    // Never ask for confirmation if there are still unselected questions
    need_confirmation = false;
  }
  if (anyUnselectedAboveSkipToPage && skipToPage) {
    // Never ask for confirmation if there are still unselected questions above the skipToPage
    need_confirmation = false;
  }
  if (page.pageType === PageTypeEnum.CALCULATOR) {
    return {
      anyUnselectedAboveSkipToPage,
      anyUnselected,
      skipToPage,
      pointsArray,
      found_selected_key,
      need_confirmation,
    };
  } else {
    return { anyUnselectedAboveSkipToPage, anyUnselected, skipToPage, found_selected_key, need_confirmation };
  }
}

// A list of possible variable names
const variableList = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z',
];

/** Return an array of variable names like A,B,C up to length 'length' */
export function formulaGetVariableList(length: number) {
  if (!length) return [];
  return variableList.slice(0, length);
}

export const allowedMathFunctions = ['min', 'max', 'sqrt', 'pow', 'exp', 'log', 'floor', 'round'] as const;
const formulaSafetyValidateRegex =
  /^([+-/*%!()?:, ]|[<>]=?|===?|[&][&]|\|\||\b[A-Z]\b|\b(min|max|sqrt|pow|exp|log|floor|round)\(|\b\d+([.]\d+)?\b)*$/;

export function runCalculatorFormula(formula: PageCalculatorType['formula'], pointsArray: number[]): number {
  if (!formula || formula.type === 'sum') return pointsArray.reduce((a, b) => a + b, 0);
  // if (formula.type === 'max') return Math.max(...pointsArray); // Just a random idea we could add
  // if (formula.type === 'min') return Math.min(...pointsArray);
  if (formula.type === 'formula') {
    const variableList = formulaGetVariableList(pointsArray.length);
    const formulaString = formula.formula;
    if (!formulaString) {
      throw new Error('No formula defined');
    }
    // Validate the formula to make sure it's safe
    if (!formulaSafetyValidateRegex.test(formulaString)) {
      throw new Error('Formula contains operations that are not allowed');
    }
    const cmdToRun = `const {${allowedMathFunctions.join(', ')}} = Math; return ${formulaString}`;
    try {
      // eslint-disable-next-line no-new-func
      var func = new Function(...variableList, cmdToRun);
    } catch (e: any) {
      console.log('Error in formula:', e.message, cmdToRun);
      if (e instanceof SyntaxError) {
        throw new Error('Syntax error in formula');
      }
      // Add the cmdToRun to the error message
      throw new Error(`Error in formula: ${e.message}: ${cmdToRun}`);
    }
    const result = func(...pointsArray);
    // Make sure result is of type number, otherwise throw an error
    if (typeof result !== 'number') {
      throw new Error('Result of formula is not a number');
    } else if (isNaN(result) || !isFinite(result)) {
      throw new Error(`Result of formula is ${result}`);
    }
    return result;
  }
  throw new Error('Unknown formula type');
}

/* Confirm to move on to the next message. Parameter should be the same as message.length - 1, and is provided
 * just to avoid possible timing issues.  It will rollback to this message just to be safe.
 * It sets message.extra.have_confirmed = true
 * and then reruns the page, displaying the next message
 */
export function confirmMessageNext(messageIndex: number, cb: RunAlgorithmCallbacks) {
  const { messages } = cb.rollbackToMessage(messageIndex);
  const message = messages[messageIndex];
  if (!message) return; // I suppose could happen in edge cases if we get multiple reducer messages
  verifyMessageIsAlgorithmPage(message);
  if (message.extra.cant_confirm_error) {
    throw new Error('confirmMessageNext: ' + message.extra.cant_confirm_error);
  }
  message.extra.have_confirmation = true;
  if (!message.pages?.[0]) {
    // If this happens in a unit test, then probably confirmMessageNext was
    // called with the wrong messageIndex
    console.log('messages:', JSON.stringify(messages, null, 2));
    console.error('confirmMessageNext: No page found for message at index', messageIndex);
    return; // should never happen?
  }
  runPage({ page: message.pages?.[0], messageIndex }, cb);
}

export function addToPagesUsedHelper(
  param: { pageId?: string; pageName?: string },
  pagesUsed: SetLike,
  nameToPageId: { [title: string]: string } | undefined
) {
  if (param.pageId) {
    pagesUsed[param.pageId] = true;
  } else if (param.pageName) {
    const link = param.pageName;
    const page_id = link[0] !== '#' ? nameToPageId?.[link] : undefined;
    pagesUsed[page_id || link] = true;
  }
}

/** A small helper function for pagesUsed, which is a Set<string> like object */
export function arrayToSetLike(array: string[]): { [key: string]: true } {
  const obj: { [key: string]: true } = {};
  array.forEach((item) => {
    obj[item] = true;
  });
  return obj;
}

export function getPageById(pageId: string, documentVersionId: string, cb: RunAlgorithmCallbacks) {
  const documentVersion = cb.getDocumentVersion(documentVersionId);
  if (!documentVersion) {
    console.error('getPageById: No document version found for id', documentVersionId);
    return undefined;
  }
  return documentVersion.pages[pageId];
}

export function getPage(pageName: string, documentVersionId: string, cb: RunAlgorithmCallbacks) {
  const documentVersion = cb.getDocumentVersion(documentVersionId);
  if (!documentVersion) {
    console.warn('getPage: No document version found for id', documentVersionId);
    return undefined;
  }
  pageName = pageName.trim();
  if (pageName.startsWith('#')) {
    // External link
    return undefined;
  }
  const pageId = documentVersion.nameToPageId[pageName];
  if (!pageId) {
    console.warn(`getPage: Page '${pageName}' not found in document ${documentVersionId} '${documentVersion.title}'`);
    return undefined;
  }
  return documentVersion.pages[pageId];
}

export function external_link_to_href(external_link: LinkSlugType): string {
  let text = external_link?.link;
  let page: string | undefined = '';
  if (text?.startsWith('#')) {
    text = text.slice(1);
    page = text.split('#')[1];
  }
  if (!external_link.link_slug) {
    return `#${encodeURI(text!)}`;
  } else {
    return `/chat?slug=${external_link.link_slug}${page ? `&page=${encodeURIComponent(page)}` : ''}`;
  }
}

export function external_link_to_algorithm_flat_href(external_link: LinkSlugType): string {
  let text = external_link?.link;
  let page: string | undefined = '';
  if (text?.startsWith('#')) {
    text = text.slice(1);
    page = text.split('#')[1];
  }
  if (!external_link.link_slug) {
    return `#${encodeURI(text!)}`;
  }
  return `/doc/edit/algorithmFlat?slug=${encodeURIComponent(external_link.link_slug)}${
    page ? `&page=${encodeURIComponent(page)}` : ''
  }`;
}
